// v2.4.0 — DeliveryMapMapbox : carte Mapbox Standard 3D photoréaliste + tracé OSRM.
// Props : { dest:[lat,lng], destCode, destLabel, pos:[lat,lng], distance, arrived, onTokenInvalid }
// onTokenInvalid : callback déclenché si Mapbox renvoie 401 → DeliveryPage bascule sur Leaflet.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { toast } from "sonner";
import useOSRMRoute from "../hooks/useOSRMRoute";
import DeliveryHUD from "@/components/DeliveryHUD";

const MAPBOX_STANDARD_STYLE = "mapbox://styles/mapbox/standard";
const MAPBOX_STREETS_FALLBACK = "mapbox://styles/mapbox/satellite-streets-v12";
// v2.18.4 — Mode nuit livreur PRO (lu depuis localStorage, écrit par NightModeToggle).
const MAPBOX_NIGHT_STYLE = "mapbox://styles/mapbox/navigation-night-v1";
function _isNightModeOn() {
  try { return typeof window !== "undefined" && window.localStorage && window.localStorage.getItem("civ_courier_night_mode") === "1"; } catch (_) { return false; }
}

function fmtDuration(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `${h}h ${rest}min`;
}

function fmtDistance(m) {
  if (m == null) return "—";
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(2)} km`;
}

// v3.6.4 — GPS livreur immersif : le bon ENGIN sur la route (moto ou voiture).
// Type persisté (localStorage) + hot-swap via l'event `aya:vehicle:changed`.
const VEHICLE_KEY = "civ_courier_vehicle";
export function getVehicleType() {
  try { return localStorage.getItem(VEHICLE_KEY) === "car" ? "car" : "moto"; } catch (_) { return "moto"; }
}

function _vehicleHTML(type) {
  if (type === "car") {
    // Berline vue de dessus, capot vers le haut (rotation Mapbox = cap).
    return `
      <div style="position:relative;width:46px;height:46px;filter:drop-shadow(0 0 10px rgba(0,255,136,0.7));">
        <svg viewBox="0 0 48 48" width="46" height="46" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 6 Q24 3 33 6 Q35.5 10 35.5 18 L35.5 36 Q35.5 43 33 44.5 Q24 47 15 44.5 Q12.5 43 12.5 36 L12.5 18 Q12.5 10 15 6 Z" fill="#00D877" stroke="#03160E" stroke-width="1.6"/>
          <rect x="15" y="5.2" width="4.4" height="2.6" rx="1.3" fill="#FFF7CC"/>
          <rect x="28.6" y="5.2" width="4.4" height="2.6" rx="1.3" fill="#FFF7CC"/>
          <path d="M16.5 15 Q24 12.5 31.5 15 L30 21 Q24 19 18 21 Z" fill="#0F172A" opacity="0.92"/>
          <rect x="17" y="21.5" width="14" height="12" rx="3.5" fill="#053B27"/>
          <path d="M17.5 38.5 Q24 41 30.5 38.5 L31 34.5 Q24 36.5 17 34.5 Z" fill="#0F172A" opacity="0.92"/>
        </svg>
      </div>`;
  }
  // Moto de livraison vue de dessus : guidon, casque, top-case.
  return `
    <div style="position:relative;width:42px;height:42px;filter:drop-shadow(0 0 10px rgba(0,255,136,0.7));">
      <svg viewBox="0 0 48 48" width="42" height="42" xmlns="http://www.w3.org/2000/svg">
        <rect x="21.4" y="3.5" width="5.2" height="9.5" rx="2.6" fill="#0B0F14" stroke="#00FF88" stroke-width="1.1"/>
        <rect x="13.5" y="12.6" width="21" height="2.8" rx="1.4" fill="#E8EDF0"/>
        <path d="M19.6 15.4 Q24 13.4 28.4 15.4 L27.8 26 Q24 28 20.2 26 Z" fill="#00D877" stroke="#03160E" stroke-width="1.2"/>
        <path d="M18.2 28 Q24 24 29.8 28 L29.2 32.4 Q24 35 18.8 32.4 Z" fill="#123B2A"/>
        <circle cx="24" cy="24.5" r="5.4" fill="#0F172A" stroke="#FFFFFF" stroke-width="1.5"/>
        <rect x="18.6" y="34.4" width="10.8" height="7.2" rx="2.2" fill="#00FF88" stroke="#03160E" stroke-width="1.2"/>
        <rect x="21.4" y="42" width="5.2" height="5.5" rx="2.6" fill="#0B0F14" stroke="#00FF88" stroke-width="1.1"/>
      </svg>
    </div>`;
}

// Caméra "conduite" façon Waze/MBUX : véhicule dans le tiers bas, cap sur la route.
const DRIVE_CAM = { zoom: 17.6, pitch: 66, offset: [0, 60] };
const DRIVE_CAM_LIVE = { zoom: 18.5, pitch: 68, offset: [0, 70] };

export default function DeliveryMapMapbox({ dest, destCode, destLabel, pos, distance, arrived, token, onTokenInvalid, outOfCI }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const destMarkerRef = useRef(null);
  const userMarkerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [styleUsed, setStyleUsed] = useState(_isNightModeOn() ? MAPBOX_NIGHT_STYLE : MAPBOX_STANDARD_STYLE);
  const tokenInvalidRef = useRef(false);

  const route = useOSRMRoute(pos, dest);

  // Init Mapbox.
  useEffect(() => {
    if (!containerRef.current || !dest) return;
    mapboxgl.accessToken = token;

    let map;
    try {
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: _isNightModeOn() ? MAPBOX_NIGHT_STYLE : MAPBOX_STANDARD_STYLE,
        center: [dest[1], dest[0]], // [lng, lat]
        zoom: 16,
        pitch: 60,
        bearing: 0,
        antialias: true,
        maxZoom: 20,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[mapbox-init-error]", err && err.message, err);
      // Fallback Leaflet UNIQUEMENT si WebGL absent / non supporté (cause irrécupérable).
      const m = (err && err.message) || "";
      if (/webgl|context/i.test(m)) {
        onTokenInvalid && onTokenInvalid("no_webgl");
      }
      return;
    }

    mapRef.current = map;
    try { window.__AYA_MAP__ = map; } catch (_) { /* debug */ }
    // v2.19.35a — Fallback style si Standard Satellite échoue (token sans accès v3).
    let _styleFallbackApplied = false;
    map.on("error", (e) => {
      try {
        const m = String(e?.error?.message || "");
        if (!_styleFallbackApplied && /style|sprite/i.test(m) && e?.error?.status !== 401 && e?.error?.status !== 403) {
          _styleFallbackApplied = true;
          console.warn("[mapbox] Standard Satellite v3 indisponible — fallback satellite-streets-v12");
          map.setStyle(MAPBOX_STREETS_FALLBACK);
        }
      } catch (_) {}
    });

    map.on("error", (e) => {
      const status = e?.error?.status;
      const msg = e?.error?.message || String(e?.error || "");
      // eslint-disable-next-line no-console
      console.error("[mapbox-runtime-error]", msg, "status:", status, "raw:", e?.error);

      // Fallback Leaflet UNIQUEMENT si erreur d'authentification EXPLICITE.
      // ATTENTION : le message d'erreur "Failed to fetch ...?access_token=pk.xxx"
      // contient littéralement "access_token=" dans l'URL → ne PAS matcher ce
      // pattern sinon on bascule à tort lors d'un fetch transitoire (le SDK
      // Mapbox retry automatiquement et finit par charger).
      const isExplicitAuthError =
        status === 401 ||
        status === 403 ||
        /\bunauthorized\b/i.test(msg) ||
        /\bnot\s+authorized\b/i.test(msg) ||
        /\binvalid\s+(access\s+)?token\b/i.test(msg) ||
        /\bforbidden\b/i.test(msg);

      if (isExplicitAuthError) {
        if (!tokenInvalidRef.current) {
          tokenInvalidRef.current = true;
          // eslint-disable-next-line no-console
          console.error("[mapbox] invalid token (status=" + status + ") — fallback Leaflet");
          onTokenInvalid && onTokenInvalid("unauthorized");
        }
      }
      // Les "Failed to fetch" transitoires sont IGNORÉS (Mapbox SDK retry tout seul).
    });

    map.on("style.load", () => {
      // v2.19.35b — Standard v3 PUR (pas satellite) : ambiance dusk + landmarks 3D iconiques.
      try {
        map.setConfigProperty("basemap", "lightPreset", "dusk");
        map.setConfigProperty("basemap", "show3dObjects", true);
        map.setConfigProperty("basemap", "showPointOfInterestLabels", true);
        map.setConfigProperty("basemap", "showRoadLabels", true);
        map.setConfigProperty("basemap", "showTransitLabels", false);
        map.setConfigProperty("basemap", "showPlaceLabels", true);
      } catch (_) { /* style != Standard v3 → ignoré */ }

      // Terrain + Sky (le style Standard les a généralement déjà).
      try {
        if (!map.getSource("mapbox-dem")) {
          map.addSource("mapbox-dem", {
            type: "raster-dem",
            url: "mapbox://mapbox.mapbox-terrain-dem-v1",
            tileSize: 512,
            maxzoom: 14,
          });
        }
        map.setTerrain({ source: "mapbox-dem", exaggeration: 1.2 });
      } catch (_) {}

      // Sky si pas dans le style.
      try {
        if (!map.getLayer("sky")) {
          map.addLayer({
            id: "sky",
            type: "sky",
            paint: {
              "sky-type": "atmosphere",
              "sky-atmosphere-sun-intensity": 15,
            },
          });
        }
      } catch (_) {}

      // 3D buildings — uniquement si style != standard (Standard les a déjà via "buildings").
      // v2.19.34a : tuning beige/sable AYA, opacity 0.85, minzoom 15 + interpolation
      // hauteur pour apparition douce entre zoom 15→15.5 (effet "émergence").
      try {
        const styleName = (map.getStyle().name || "").toLowerCase();
        if (!styleName.includes("standard") && !map.getLayer("civ-3d-buildings")) {
          const layers = map.getStyle().layers || [];
          const labelLayer = layers.find((l) => l.type === "symbol" && l.layout && l.layout["text-field"]);
          map.addLayer(
            {
              id: "civ-3d-buildings",
              source: "composite",
              "source-layer": "building",
              filter: ["==", "extrude", "true"],
              type: "fill-extrusion",
              minzoom: 15,
              paint: {
                "fill-extrusion-color": "#e8e3dd",
                "fill-extrusion-height": [
                  "interpolate", ["linear"], ["zoom"],
                  15, 0,
                  15.5, ["get", "height"],
                ],
                "fill-extrusion-base": [
                  "interpolate", ["linear"], ["zoom"],
                  15, 0,
                  15.5, ["get", "min_height"],
                ],
                "fill-extrusion-opacity": 0.85,
              },
            },
            labelLayer?.id,
          );
        }
      } catch (_) {}

      setMapReady(true);
    });

    return () => {
      try { map.remove(); } catch (_) {}
      mapRef.current = null;
    };
  }, [token, dest && dest[0], dest && dest[1]]); // eslint-disable-line

  // Marker destination.
  useEffect(() => {
    if (!mapReady || !dest || !mapRef.current) return;
    if (destMarkerRef.current) destMarkerRef.current.remove();
    const el = document.createElement("div");
    el.style.cssText = "width:28px;height:28px;border-radius:50%;background:#dc2626;border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,0.5);";
    el.setAttribute("data-testid", "mapbox-marker-dest");
    const popup = new mapboxgl.Popup({ offset: 16 }).setHTML(
      `<div style="font-size:12px"><strong>${destLabel || destCode || "Destination"}</strong>${destCode ? `<br/><code>${destCode}</code>` : ""}</div>`,
    );
    destMarkerRef.current = new mapboxgl.Marker({ element: el })
      .setLngLat([dest[1], dest[0]])
      .setPopup(popup)
      .addTo(mapRef.current);
  }, [mapReady, dest, destCode, destLabel]);

  // Marker user.
  // v3.6.4 — ENGIN néon (moto/voiture) posé à plat sur la route (pitchAlignment map).
  useEffect(() => {
    if (!mapReady || !pos || !mapRef.current) return;
    if (!userMarkerRef.current) {
      const el = document.createElement("div");
      el.setAttribute("data-testid", "mapbox-marker-user");
      el.setAttribute("data-vehicle", getVehicleType());
      el.innerHTML = _vehicleHTML(getVehicleType());
      userMarkerRef.current = new mapboxgl.Marker({ element: el, rotationAlignment: "map", pitchAlignment: "map" })
        .setLngLat([pos[1], pos[0]])
        .addTo(mapRef.current);
    } else if (!liveNavRef.current?.active) {
      // v2.19.35d — Skip update GPS quand live nav actif : le follow loop écrase la position
      // avec la valeur snappée/simulée sur la polyline, sinon le marker repart sur le GPS brut.
      userMarkerRef.current.setLngLat([pos[1], pos[0]]);
    }
  }, [mapReady, pos]);

  // Hot-swap moto ↔ voiture depuis le cockpit (event aya:vehicle:changed).
  useEffect(() => {
    const onVehicle = (e) => {
      const type = e?.detail?.vehicle === "car" ? "car" : "moto";
      const el = userMarkerRef.current?.getElement?.();
      if (el) { el.innerHTML = _vehicleHTML(type); el.setAttribute("data-vehicle", type); }
    };
    window.addEventListener("aya:vehicle:changed", onVehicle);
    return () => window.removeEventListener("aya:vehicle:changed", onVehicle);
  }, []);

  // v2.19.35b/c — 3D Live Navigation : caméra immersive en mode "course active".
  // v2.19.35c : snap puck sur polyline OSRM + bearing = azimut du segment courant + pitch 60.
  const liveNavRef = useRef({ active: false, segmentIdx: 0, intervalId: null });

  // Helpers géo lite (pas de dep turf).
  const _toRad = (d) => (d * Math.PI) / 180;
  const _toDeg = (r) => (r * 180) / Math.PI;
  const _bearingDeg = (a, b) => {
    // a/b : [lng, lat]. Bearing initial du segment a→b.
    const φ1 = _toRad(a[1]), φ2 = _toRad(b[1]);
    const Δλ = _toRad(b[0] - a[0]);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (_toDeg(Math.atan2(y, x)) + 360) % 360;
  };
  const _distSqLL = (a, b) => {
    // Approx 2D pour comparaison locale (suffisant pour nearest).
    const dx = (a[0] - b[0]); const dy = (a[1] - b[1]);
    return dx * dx + dy * dy;
  };
  const _projectOnSegment = (p, a, b) => {
    // Projection 2D approximée (lng/lat ~ plan local). Retourne {point, t in [0,1]}.
    const apx = p[0] - a[0], apy = p[1] - a[1];
    const abx = b[0] - a[0], aby = b[1] - a[1];
    const ab2 = abx * abx + aby * aby || 1e-12;
    let t = (apx * abx + apy * aby) / ab2;
    t = Math.max(0, Math.min(1, t));
    return { point: [a[0] + abx * t, a[1] + aby * t], t };
  };
  const _nearestOnPolyline = (pos2, coords) => {
    if (!coords || coords.length < 2) return null;
    let best = { dist: Infinity, point: coords[0], segIdx: 0, t: 0 };
    for (let i = 0; i < coords.length - 1; i++) {
      const pr = _projectOnSegment(pos2, coords[i], coords[i + 1]);
      const d = _distSqLL(pos2, pr.point);
      if (d < best.dist) best = { dist: d, point: pr.point, segIdx: i, t: pr.t };
    }
    return best;
  };
  // v2.19.35d — Helpers pour simulation "rail" : avance le long de la polyline en mètres.
  const _haversineM = (a, b) => {
    const R = 6371000;
    const dLat = _toRad(b[1] - a[1]);
    const dLon = _toRad(b[0] - a[0]);
    const lat1 = _toRad(a[1]), lat2 = _toRad(b[1]);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x));
  };
  const _polylineTotalLength = (coords) => {
    if (!coords || coords.length < 2) return 0;
    let total = 0;
    for (let i = 0; i < coords.length - 1; i++) total += _haversineM(coords[i], coords[i + 1]);
    return total;
  };
  // Retourne {point, bearing, segmentIdx, finished} pour une distance cumulée donnée.
  const _advanceAlongPolyline = (coords, progressMeters) => {
    if (!coords || coords.length < 2) return null;
    let remaining = Math.max(0, progressMeters);
    for (let i = 0; i < coords.length - 1; i++) {
      const a = coords[i], b = coords[i + 1];
      const segLen = _haversineM(a, b);
      if (remaining <= segLen || i === coords.length - 2) {
        const t = segLen > 0 ? Math.min(1, remaining / segLen) : 0;
        const point = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
        const bearing = _bearingDeg(a, b);
        const finished = i === coords.length - 2 && remaining >= segLen;
        return { point, bearing, segmentIdx: i, finished };
      }
      remaining -= segLen;
    }
    const last = coords[coords.length - 1], prev = coords[coords.length - 2];
    return { point: last, bearing: _bearingDeg(prev, last), segmentIdx: coords.length - 2, finished: true };
  };

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;

    const enterLiveNav = () => {
      if (liveNavRef.current.active) return;
      liveNavRef.current.active = true;
      liveNavRef.current.segmentIdx = 0;

      const coords = route?.coords;
      const totalLen = _polylineTotalLength(coords);
      // v2.19.35d — Initialise progress en partant du point snappé du GPS sur la polyline,
      // converti en mètres parcourus depuis le départ.
      let progressMeters = 0;
      if (coords && coords.length >= 2 && pos) {
        const snap = _nearestOnPolyline([pos[1], pos[0]], coords);
        if (snap) {
          // Cumule longueur des segments avant + fraction du segment courant.
          for (let i = 0; i < snap.segIdx; i++) {
            progressMeters += _haversineM(coords[i], coords[i + 1]);
          }
          const segLen = _haversineM(coords[snap.segIdx], coords[snap.segIdx + 1]);
          progressMeters += segLen * snap.t;
        }
      }
      liveNavRef.current.progressMeters = progressMeters;
      liveNavRef.current.totalLen = totalLen;

      // v2.19.36 — Chantier 3 : si GPS device dispo, watchPosition pour snap réel.
      // Mode "real" : puck cyan si <50m de la route, orange si hors-route (>50m).
      const TARGET_DURATION_SEC = 180;
      liveNavRef.current.mode = "simulate";
      liveNavRef.current.watchId = null;
      liveNavRef.current.realGps = null;
      try {
        if (typeof navigator !== "undefined" && navigator.geolocation) {
          // Timeout 5s : si pas de fix GPS, on reste en simulate.
          const realStart = Date.now();
          liveNavRef.current.watchId = navigator.geolocation.watchPosition(
            (p) => {
              const lat = p?.coords?.latitude, lng = p?.coords?.longitude;
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
              liveNavRef.current.realGps = [lng, lat];
              if (liveNavRef.current.mode === "simulate" && Date.now() - realStart < 5000) {
                liveNavRef.current.mode = "real";
              }
            },
            () => { /* permission refusée ou erreur → reste en simulate */ },
            { enableHighAccuracy: true, maximumAge: 2000, timeout: 5000 },
          );
        }
      } catch (_) { /* noop */ }

      const SPEED_MPS = totalLen > 0 ? Math.max(8, Math.min(50, totalLen / TARGET_DURATION_SEC)) : 25;
      const TICK_MS = 1200;
      const DELTA_M_PER_TICK = SPEED_MPS * (TICK_MS / 1000);
      liveNavRef.current.speedMps = SPEED_MPS;

      const applyAdvance = () => {
        const c = route?.coords;
        if (!c || c.length < 2) return;
        // v2.19.36 — Mode "real" : utilise GPS device snappé sur polyline. Sinon simulate.
        let useRealGps = false;
        let advPoint, advBearing, finished = false, segmentIdx = 0;
        let offRoute = false;
        if (liveNavRef.current.mode === "real" && liveNavRef.current.realGps) {
          useRealGps = true;
          const gps = liveNavRef.current.realGps;
          const snap = _nearestOnPolyline(gps, c);
          if (snap) {
            const snapDistM = _haversineM(gps, snap.point);
            offRoute = snapDistM >= 50;
            if (offRoute) {
              advPoint = gps;
              const a = c[snap.segIdx], b = c[Math.min(snap.segIdx + 1, c.length - 1)];
              advBearing = _bearingDeg(a, b);
            } else {
              advPoint = snap.point;
              const a = c[snap.segIdx], b = c[Math.min(snap.segIdx + 1, c.length - 1)];
              advBearing = _bearingDeg(a, b);
            }
            segmentIdx = snap.segIdx;
            // Calcule progressMeters depuis snap pour HUD cohérent.
            let progress = 0;
            for (let i = 0; i < snap.segIdx; i++) progress += _haversineM(c[i], c[i + 1]);
            const segLen = _haversineM(c[snap.segIdx], c[snap.segIdx + 1]);
            progress += segLen * snap.t;
            liveNavRef.current.progressMeters = progress;
          }
        }
        if (!useRealGps) {
          const adv = _advanceAlongPolyline(c, liveNavRef.current.progressMeters);
          if (!adv) return;
          advPoint = adv.point; advBearing = adv.bearing; finished = adv.finished; segmentIdx = adv.segmentIdx;
        }
        // v3.6.4 — Glow de l'engin : vert néon par défaut, ambre si offRoute.
        try {
          const el = userMarkerRef.current?.getElement?.();
          const wrap = el?.firstElementChild;
          if (wrap) {
            wrap.style.filter = offRoute
              ? "drop-shadow(0 0 10px rgba(255,159,28,0.8))"
              : "drop-shadow(0 0 10px rgba(0,255,136,0.7))";
          }
        } catch (_) { /* noop */ }
        try { userMarkerRef.current?.setLngLat(advPoint); } catch (_) { /* noop */ }
        try { userMarkerRef.current?.setRotation(advBearing); } catch (_) { /* noop */ }
        map.easeTo({ center: advPoint, bearing: advBearing, pitch: DRIVE_CAM_LIVE.pitch, zoom: DRIVE_CAM_LIVE.zoom, offset: DRIVE_CAM_LIVE.offset, duration: 800 });
        // Dispatch event progress pour brancher le HUD.
        const remainingMeters = Math.max(0, liveNavRef.current.totalLen - liveNavRef.current.progressMeters);
        const etaSec = liveNavRef.current.speedMps > 0 ? remainingMeters / liveNavRef.current.speedMps : 0;
        try {
          // v3.6.5 — Prochaine manœuvre (turn-by-turn) pour le cockpit.
          let nextTurn = null;
          try {
            const mans = maneuversRef.current;
            if (mans && mans.length) {
              const prog = liveNavRef.current.progressMeters;
              const nm = mans.find((mn) => mn.cum > prog + 3 && mn.type !== "depart");
              if (nm) nextTurn = { type: nm.type, modifier: nm.modifier, exit: nm.exit, name: nm.name, inMeters: Math.max(0, Math.round(nm.cum - prog)) };
            }
          } catch (_) { /* noop */ }
          window.dispatchEvent(new CustomEvent("aya:nav:progress", { detail: {
            remainingMeters, etaSec, totalLength: liveNavRef.current.totalLen,
            progressMeters: liveNavRef.current.progressMeters, offRoute, mode: liveNavRef.current.mode,
            speedMps: liveNavRef.current.speedMps,
            nextTurn,
          } }));
        } catch (_) { /* noop */ }
        if (typeof window !== "undefined" && window.__AYA_DEBUG_NAV__) {
          // eslint-disable-next-line no-console
          console.log(`[AYA_NAV] mode=${liveNavRef.current.mode} progress=${liveNavRef.current.progressMeters.toFixed(1)}m / ${liveNavRef.current.totalLen.toFixed(1)}m segIdx=${segmentIdx} offRoute=${offRoute} finished=${finished}`);
        }
        if (finished) {
          if (liveNavRef.current.intervalId) {
            clearInterval(liveNavRef.current.intervalId);
            liveNavRef.current.intervalId = null;
          }
          try { window.dispatchEvent(new CustomEvent("aya:nav:end", {})); } catch (_) { /* noop */ }
          try { toast.success("📍 Arrivé à destination"); } catch (_) { /* noop */ }
        }
      };

      // Premier paint + zoom-in initial.
      applyAdvance();

      // Boucle : avance le puck le long de la polyline (mode "rail" simulé).
      liveNavRef.current.intervalId = setInterval(() => {
        liveNavRef.current.progressMeters = Math.min(
          liveNavRef.current.totalLen,
          liveNavRef.current.progressMeters + DELTA_M_PER_TICK,
        );
        applyAdvance();
      }, TICK_MS);
    };

    const exitLiveNav = () => {
      if (!liveNavRef.current.active) return;
      liveNavRef.current.active = false;
      if (liveNavRef.current.intervalId) {
        clearInterval(liveNavRef.current.intervalId);
        liveNavRef.current.intervalId = null;
      }
      // v2.19.36 — Cleanup watchPosition GPS device.
      try {
        if (liveNavRef.current.watchId != null && navigator?.geolocation?.clearWatch) {
          navigator.geolocation.clearWatch(liveNavRef.current.watchId);
        }
      } catch (_) { /* noop */ }
      liveNavRef.current.watchId = null;
      liveNavRef.current.realGps = null;
      liveNavRef.current.mode = "simulate";
      try { window.dispatchEvent(new CustomEvent("aya:nav:end", {})); } catch (_) { /* noop */ }
      try { userMarkerRef.current?.setRotation(0); } catch (_) { /* noop */ }
      // Retour vue plan : pitch 60, recentre tout.
      try {
        const bounds = new mapboxgl.LngLatBounds();
        if (pos) bounds.extend([pos[1], pos[0]]);
        if (dest) bounds.extend([dest[1], dest[0]]);
        if (route.coords) route.coords.forEach((c) => bounds.extend(c));
        if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 80, pitch: 60, bearing: 0, duration: 1200 });
        else map.easeTo({ pitch: 60, zoom: 16, bearing: 0, duration: 1000 });
      } catch (_) { map.easeTo({ pitch: 60, zoom: 16, bearing: 0, duration: 1000 }); }
    };

    const onCourseChanged = (e) => {
      if (e?.detail?.active) enterLiveNav(); else exitLiveNav();
    };
    const onToggle = () => {
      if (liveNavRef.current.active) exitLiveNav(); else enterLiveNav();
    };
    window.addEventListener("civ-courier-course-changed", onCourseChanged);
    window.addEventListener("aya-live-nav-toggle", onToggle);
    return () => {
      window.removeEventListener("civ-courier-course-changed", onCourseChanged);
      window.removeEventListener("aya-live-nav-toggle", onToggle);
      if (liveNavRef.current.intervalId) clearInterval(liveNavRef.current.intervalId);
    };
  }, [mapReady, pos, dest, route.coords]);

  // Tracé OSRM.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !route.coords) return;
    const map = mapRef.current;
    const geo = {
      type: "Feature",
      geometry: { type: "LineString", coordinates: route.coords },
      properties: {},
    };
    if (map.getSource("civ-route")) {
      map.getSource("civ-route").setData(geo);
    } else {
      map.addSource("civ-route", { type: "geojson", data: geo });
      // v2.19.35c — slot:middle pour Standard v3 : insère la route SOUS les labels mais AU-DESSUS
      // du sol/buildings 3D natifs, sinon les extrusions masquent la polyline en pitch 70.
      // v3.6.3 — "Mercedes cockpit" : GROS TRAIT VERT NÉON — 3 couches
      // (halo lumineux large + liseré sombre de contraste + cœur néon éclatant).
      const glowLayer = {
        id: "civ-route-glow",
        type: "line",
        source: "civ-route",
        slot: "middle",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#00FF88", "line-width": 30, "line-opacity": 0.28, "line-blur": 10, "line-emissive-strength": 1 },
      };
      const casingLayer = {
        id: "civ-route-casing",
        type: "line",
        source: "civ-route",
        slot: "middle",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#032117", "line-width": 15, "line-opacity": 0.92, "line-emissive-strength": 0.4 },
      };
      const mainLayer = {
        id: "civ-route-line",
        type: "line",
        source: "civ-route",
        slot: "middle",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#00FF88", "line-width": 9, "line-opacity": 1, "line-blur": 0.3, "line-emissive-strength": 1 },
      };
      try { map.addLayer(glowLayer); } catch (_) { delete glowLayer.slot; map.addLayer(glowLayer); }
      try { map.addLayer(casingLayer); } catch (_) { delete casingLayer.slot; map.addLayer(casingLayer); }
      try { map.addLayer(mainLayer); } catch (_) { delete mainLayer.slot; map.addLayer(mainLayer); }
    }
    if (route.status === "fallback") {
      toast("Itinéraire approximatif (OSRM indisponible)", { duration: 3500 });
    }
  }, [mapReady, route.coords, route.status]);

  // Recentre l'itinéraire complet.
  const recenterAll = useCallback(() => {
    if (!mapRef.current || !pos || !dest) return;
    // v2.19.35d — Si live nav actif, recentre sur le point d'avancement actuel le long
    // de la polyline (rail simulé), avec bearing du segment courant. Conserve l'animation.
    if (liveNavRef.current?.active && route.coords && route.coords.length >= 2) {
      const adv = _advanceAlongPolyline(route.coords, liveNavRef.current.progressMeters || 0);
      if (adv) {
        try { userMarkerRef.current?.setLngLat(adv.point); } catch (_) { /* noop */ }
        try { userMarkerRef.current?.setRotation(adv.bearing); } catch (_) { /* noop */ }
        mapRef.current.easeTo({ center: adv.point, bearing: adv.bearing, pitch: DRIVE_CAM_LIVE.pitch, zoom: DRIVE_CAM_LIVE.zoom, offset: DRIVE_CAM_LIVE.offset, duration: 800 });
        return;
      }
    }
    // v3.6.4 — Hors live nav : vue CONDUITE (véhicule tiers bas, cap sur la route),
    // plus de vue satellite lointaine — la route est reine.
    if (route.coords && route.coords.length >= 2) {
      const snap = _nearestOnPolyline([pos[1], pos[0]], route.coords);
      const i = snap ? snap.segIdx : 0;
      const b = _bearingDeg(route.coords[i], route.coords[Math.min(i + 1, route.coords.length - 1)]);
      mapRef.current.easeTo({ center: [pos[1], pos[0]], bearing: b, pitch: DRIVE_CAM.pitch, zoom: DRIVE_CAM.zoom, offset: DRIVE_CAM.offset, duration: 1000 });
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    bounds.extend([pos[1], pos[0]]);
    bounds.extend([dest[1], dest[0]]);
    if (route.coords) route.coords.forEach((c) => bounds.extend(c));
    mapRef.current.fitBounds(bounds, { padding: 80, pitch: 60, duration: 1200 });
  }, [pos, dest, route.coords]);

  // v3.6.5 — Manœuvres OSRM projetées sur la polyline (distance cumulée).
  const maneuversRef = useRef([]);
  useEffect(() => {
    maneuversRef.current = [];
    if (!route.coords || route.coords.length < 2 || !Array.isArray(route.steps) || !route.steps.length) return;
    try {
      const cums = [0];
      for (let i = 1; i < route.coords.length; i++) cums.push(cums[i - 1] + _haversineM(route.coords[i - 1], route.coords[i]));
      maneuversRef.current = route.steps.map((s) => {
        const snap = _nearestOnPolyline(s.loc, route.coords);
        if (!snap) return null;
        const segLen = _haversineM(route.coords[snap.segIdx], route.coords[Math.min(snap.segIdx + 1, route.coords.length - 1)]) || 0;
        return { cum: cums[snap.segIdx] + (snap.t || 0) * segLen, type: s.type, modifier: s.modifier, exit: s.exit, name: s.name };
      }).filter(Boolean).sort((a, b) => a.cum - b.cum);
    } catch (_) { maneuversRef.current = []; }
  }, [route.coords, route.steps]);

  // v3.6.4 — Entrée en scène "conduite" : dès que la route est connue, la caméra
  // plonge derrière l'engin (immersion GPS livreur), une seule fois par montage.
  const droveInRef = useRef(false);
  useEffect(() => {
    if (!mapReady || !mapRef.current || !pos || !route.coords || route.coords.length < 2) return;
    if (droveInRef.current) return;
    droveInRef.current = true;
    try {
      const snap = _nearestOnPolyline([pos[1], pos[0]], route.coords);
      const i = snap ? snap.segIdx : 0;
      const b = _bearingDeg(route.coords[i], route.coords[Math.min(i + 1, route.coords.length - 1)]);
      try { userMarkerRef.current?.setRotation(b); } catch (_) { /* noop */ }
      mapRef.current.easeTo({ center: [pos[1], pos[0]], bearing: b, pitch: DRIVE_CAM.pitch, zoom: DRIVE_CAM.zoom, offset: DRIVE_CAM.offset, duration: 1800 });
    } catch (_) { /* noop */ }
  }, [mapReady, pos, route.coords]);

  const refreshRoute = useCallback(() => {
    // Le hook se déclenche sur les deps pos/dest, ici on force un petit nudge via la sortie.
    if (!mapRef.current || !pos) return;
    toast("Itinéraire rafraîchi");
    // Re-pan vers la dest pour signaler le rafraîchissement.
    mapRef.current.easeTo({ center: [pos[1], pos[0]], duration: 600 });
  }, [pos]);

  const etaSec = route.duration_s;
  const remainingM = useMemo(() => {
    if (distance != null) return distance;
    return route.distance_m;
  }, [distance, route.distance_m]);

  return (
    <div className="civ-deliv-page civ-deliv-mapbox" data-testid="delivery-page" data-renderer="mapbox">
      {outOfCI && (
        <div data-testid="out-of-ci-banner" style={{
          position: "absolute", top: 0, left: 0, right: 0, zIndex: 10,
          background: "rgba(245,158,11,0.95)", color: "#0F172A",
          padding: "10px 16px", fontSize: 12, fontWeight: 600, textAlign: "center",
          borderBottom: "2px solid #B45309",
        }}>
          📡 Tu n'es pas en Côte d'Ivoire — l'itinéraire part du Plateau d'Abidjan. Ouvre cette page depuis ton mobile en CI pour un trajet réel.
        </div>
      )}
      <a href="/" className="civ-deliv-back" data-testid="delivery-back" style={{
        position: "absolute", top: outOfCI ? 56 : 16, left: 16, zIndex: 5,
        background: "rgba(15,23,42,0.85)", color: "#fff",
        padding: "8px 14px", borderRadius: 999, textDecoration: "none",
        fontWeight: 600, fontSize: 14, border: "1px solid rgba(255,255,255,0.15)",
        backdropFilter: "blur(8px)",
      }}>← Chat</a>

      <div ref={containerRef} data-testid="delivery-mapbox-canvas" style={{ position: "absolute", inset: 0 }} />

      {/* HUD livreur — v2.18.4d composant partagé (Mapbox + Leaflet). */}
      <DeliveryHUD
        engine="mapbox"
        remainingM={remainingM}
        etaSec={etaSec}
        routeStatus={route.status}
        destCode={destCode}
        destLabel={destLabel}
        arrived={arrived}
        onRecenter={recenterAll}
        onRefreshRoute={refreshRoute}
        engineFootnote={`Mapbox · ${styleUsed.includes("standard") ? "Standard 3D" : "Streets 3D"}`}
      />
    </div>
  );
}
